<template>
  <w-sliding-popup ref="slidingPopup" :title="rscSettingTitle" width="650px" :default-content="false" @close="close">
    <div slot="content" class="body-content-no-scroll" style="height:100%;">
      <div v-loading="isloading">
        <el-row class="title-bar mr-b10" type="flex" justify="space-between">
          <div class="vertical-center-box">
            <!-- [{{analysisDateRange}}] -->
            <span class="title-h4 mr-r10">장애 예측 분석 결과 </span>
          </div>
        </el-row>
        <!-- 장애예측분석 결과 -->
        <div class="condition-set-box mr-b25">
          <span class="high">
            [{{selectPredictResultObj.objName}}] {{selectPredictResultObj.rsctypeName}} > {{selectPredictResultObj.rscName}} ({{selectPredictResultObj.rscKey}})
              의 목표치는 <span style="color: red;">{{ selectPredictResultObj.conditionValue }}</span> 입니다.
          </span>
          <div class="mr-t10">
            예상 도달 시점: {{convertDateString(null, null, selectPredictResultObj.predictCheckDatetime)}}
          </div>
        </div>
        <div class="mr-b25">
          <highcharts :options="options" ref="predictResultChart" />
        </div>
      </div>
    </div>
  </w-sliding-popup>
</template>

<script>
import { convertValuByUnit, convertValueByUnitData } from '@/utils/number';
import { isNil, cloneDeep } from 'lodash';
import EventRptApi from '@/api/event/event-rpt';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import msgUtil from '@/utils/message';
// import { lineOptions } from '@/constants/chart-options';

// 장애 예측 분석결과 차트 옵션
const lineOptions = {
  credits: {
    enabled: false,
  },
  chart: {
    type: 'line',
    height: 250,
  },
  title: {
    text: undefined,
  },
  legend: {
    enabled: false,
  },
  xAxis: {
    type: 'datetime',
    alignTick: false,
    startOnTick: true,
    labels: {
      format: '{value: %Y-%m-%d}',
      style: {
        color: '$chinagray',
        fontSize: '9px',
      },
    },
    crosshair: true,
  },
  yAxis: {
    title: false,
    minTickInterval: 0,
    startOnTick: false,
    lineWidth: 2,
  },
  series: [{
    turboThreshold: 10000,
    data: [],
  }],
};

export default {
  name: 'WRedictResultChartPopup',
  props: {
    selectPredictResultObj: {
      type: Object,
    },
  },
  data() {
    return {
      title: '장애 예측 분석 차트',
      options: cloneDeep(lineOptions),
      isloading: false,
      analysisDateRange: '',
      chartData: [],
    };
  },
  computed: {
    /**
     * 예측 자원 타이틀
     * @author Song
     */
    rscSettingTitle() {
      const title = '예측 분석 결과 차트';
      return isNil(this.selectPredictResultObj) ? title : `[${this.selectPredictResultObj.objName}] ${title}`;
    },
  },
  watch: {
    selectPredictResultObj: {
      handler(value) {
        if (!isNil(value)) {
          this.options = cloneDeep(lineOptions); // 옵션 초기화
          this.loadChartInfo();
        }
      },
    },
  },
  methods: {
    open() {
      this.$refs.slidingPopup.open();
      console.log(this.selectPredictResultObj);
    },
    close() {
      this.$refs.slidingPopup.visible = false;
    },
    /**
     * 분석 결과 차트 조회 및 차트 plotline 옵션 수정 (목표치)
     * @author Song
     */
    loadChartInfo() {
      this.isloading = true;
      // 데이터 로드
      EventRptApi.getTargetRptAnalysisChartData({ hashkeyIdString: this.selectPredictResultObj.hashkeyIdString }).then((res) => {
        if (res.data.success) {
          this.chartData = res.data.data;
          this.makeChartSkeleton();
        }
      }).catch((e) => {
        this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '분석 결과 차트', false));
        throw new Error(e);
      }).finally(() => {
        this.isloading = false;
      });
    },
    /**
     * 차트 옵션 수정 (목표치)
     * @author Song
     */
    makeChartSkeleton() {
      const me = this;
      const rawChartData = me.chartData;
      // 이벤트 색상에 따라 라인색 지정?
      const chartOptionInfo = cloneDeep(me.options);
      const plotLinesInfo = [];
      plotLinesInfo.push({
        // color: doubleCondition.eventlevelColorBg,
        color: 'red',
        width: 1.5,
        dashStyle: 'dot',
        value: me.selectPredictResultObj.conditionValue,
        zIndex: 4,
      });
      const plotLinesInfoX = [];
      plotLinesInfoX.push({
        // color: doubleCondition.eventlevelColorBg,
        color: 'red',
        width: 1.5,
        dashStyle: 'dot',
        value: isNil(me.selectPredictResultObj.predictCheckDatetime) ? null : Date.parse(me.selectPredictResultObj.predictCheckDatetime),
        zIndex: 4,
      });
      if (!isNil(rawChartData)) {
        chartOptionInfo.tooltip = {
          split: false,
          shared: true,
          valueDecimals: 2,
          formatter() {
            let tooltip = '';
            this.points.forEach((point) => {
              const unit = me.selectPredictResultObj.rscUnit ? me.selectPredictResultObj.rscUnit : null;
              const yValue = unit === 'count' ? convertValueByUnitData(point.y, 'count', '개', 1, null, null, true) : convertValuByUnit(point.y, unit, true);
              tooltip += `시간 : ${getDateToString(point.x, 'YYYY-MM-DD')}<br>값 : ${yValue}`;
            });
            return tooltip;
          },
        };
        chartOptionInfo.yAxis.labels = {
          formatter() {
            const unit = me.selectPredictResultObj.rscUnit ? me.selectPredictResultObj.rscUnit : null;
            return convertValuByUnit(this.value, unit, true, true);
          },
          style: {
            color: '$luckygray',
            fontSize: '9px',
          },
        };
      }
      chartOptionInfo.xAxis.plotLines = plotLinesInfoX;
      chartOptionInfo.yAxis.plotLines = plotLinesInfo;
      chartOptionInfo.series[0].data = cloneDeep(rawChartData);
      me.options = cloneDeep(chartOptionInfo);
    },
    getTableValueAfterCheckEmpty(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    /**
     * 장애 예측 분석 - 문자열 날짜포맷 변경
     * @author Song
     */
    convertDateString(row, column, value) {
      return isNil(value) ? EMPTY_TEXT : getDateToString(Date.parse(value), 'YYYY-MM-DD');
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/event/components/popup/PredictResultChartPopup.vue